import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import GoldParticles from "@/components/GoldParticles";

const TIER_ORDER = ["Title Partner", "Gold Partner", "Silver Partner", "Media Partner"];

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
  }, []);

  const tiers = TIER_ORDER.map((tier) => ({ tier, items: sponsors.filter((s) => s.tier === tier) })).filter(
    (t) => t.items.length > 0
  );
  const extraTiers = [...new Set(sponsors.map((s) => s.tier))].filter((t) => !TIER_ORDER.includes(t));
  extraTiers.forEach((tier) => tiers.push({ tier, items: sponsors.filter((s) => s.tier === tier) }));

  return (
    <div data-testid="sponsors-page">
      <section className="relative overflow-hidden border-b border-white/5 py-24 sm:py-28">
        <GoldParticles density={30} />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-5">The Backers</p>
            <h1 data-testid="sponsors-heading" className="max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-[#F6F4EE]">
              The Houses That<br />
              <span className="text-gold-sheen">Back the Table.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-sm sm:text-base leading-relaxed text-[#A19E95]">
              FINATEX'26 is made possible by partners who understand that the finest return on investment is
              the next generation of finance leadership.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
        {tiers.map(({ tier, items }, ti) => (
          <div key={tier} className="mb-16 last:mb-0" data-testid={`sponsor-tier-${ti}`}>
            <Reveal>
              <p className="eyebrow mb-8">{tier}</p>
            </Reveal>
            <div className={`grid gap-5 ${ti === 0 ? "sm:grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
              {items.map((s, i) => (
                <Reveal key={s.id} delay={i * 0.07}>
                  <div
                    data-testid={`sponsor-card-${tier.replace(/\s+/g, "-").toLowerCase()}-${i}`}
                    className={`group flex items-center justify-center border border-white/8 bg-[#13131A] px-8 transition-colors duration-500 hover:border-[#D4AF37]/40 ${
                      ti === 0 ? "py-14" : "py-10"
                    }`}
                  >
                    <span className={`font-display font-semibold tracking-wide text-[#F6F4EE]/85 transition-colors duration-500 group-hover:text-[#E5C97A] ${ti === 0 ? "text-3xl sm:text-4xl" : "text-xl sm:text-2xl"}`}>
                      {s.name}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        ))}

        <Reveal>
          <div className="mt-20 border border-[#D4AF37]/25 bg-[#0B0B0F] p-10 sm:p-14 text-center">
            <SectionHeading
              eyebrow="Partnership"
              title="Put Your Name on the Table"
              copy="Sponsorship decks, stall placements and jury associations are open for the 2026 edition. Write to the organising committee for the partnership prospectus."
              testId="sponsors-cta-heading"
            />
            <Link
              to="/contact"
              data-testid="sponsors-contact-cta"
              className="group mt-8 inline-flex items-center gap-3 border border-[#D4AF37]/60 px-8 py-4 text-xs uppercase tracking-[0.22em] text-[#D4AF37] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#08080A]"
            >
              Become a Partner
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
